import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Navbar from "../../components/Navbar";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AOS from "aos";
import "aos/dist/aos.css";
import ReactPlayer from "react-player";
import PrimaryBtn from "../../components/PrimaryBtn";
import Slider from "react-slick";
import { MdOutlineReplay } from "react-icons/md";
import Popup from "reactjs-popup";
import DemoContact from "../../components/DemoContact";
import Footer from "../../components/Footer";
import carousel1 from "../../assets/videos/carousel_1.gif";
import carousel2 from "../../assets/videos/carousel_2.mp4";
import carousel3 from "../../assets/videos/carousel_3.gif";
import carousel4 from "../../assets/videos/carousel_4.mp4";
import SolutionsTextSection from "../../components/SolutionsTextSection";
import ContactFooter from "../../components/ContactFooter";

gsap.registerPlugin(ScrollTrigger);

const SolutionsList = [
  {
    id: 1,
    text: "Best-in-class outcome prediction",
    title: "Best-in-class outcome prediction algorithms",
    description:
      "Our clinical and data experts have years of experience in mapping the patient journey from diagnosis to post-treatment across more than 20 diseases.",
    img: carousel1,
  },
  {
    id: 2,
    text: "More accurate trial designs",
    title: "More accurate trial designs",
    description:
      "Measure disparities in care in a disease down to the individual physician",
    video: carousel2,
  },
  {
    id: 3,
    text: "Improved patient allocations",
    title: "Improved patient allocations",
    description:
      "Tailor messaging based on profiles to bring the most impactful messages to the right audiences to influence care",
    img: carousel3,
  },
  {
    id: 4,
    text: "Revive opportunities",
    title: "Revive opportunities",
    description: "Identify trials where imbalances led to false results",
    video: carousel4,
  },
];

export default function Research() {
  const video1ref = useRef(null);
  const isAnimationPlayed = useRef(false);
  const [currentToggle, setCurrentToggle] = useState(0);
  const [showEnded, setShowEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [popupOpen, setPopup] = useState(false);
  const sliderRef = useRef(null);

  useEffect(() => {
    AOS.init();
    SolutionsList.forEach((item) => {
      gsap.fromTo(
        `#solution-${item.id}`,
        { clipPath: "inset(0% 0% 100% 0)" },
        {
          clipPath: "inset(0% 0% 0% 0)",
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: `#solution-${item.id}`,
            start: "top 70%",
            end: "top 100%",
            scrub: 1,
            pin: false,
            pinSpacing: false,
          },
        }
      );
    });
  }, []);

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: true,
      speed: 500,
      autoplay: false,
      autoplaySpeed: 3000,
      slidesToShow: 1,
      slidesToScroll: 1,
      afterChange: (e) => {
        setCurrentToggle(e);
      },
    }),
    []
  );

  const handleReady = useCallback(() => {
    if (isAnimationPlayed.current) return;
    isAnimationPlayed.current = true;
    gsap.fromTo(
      `#introVideo`,
      { scale: 0 },
      { scale: 1.2, ease: "power2.inOut" }
    );
  }, []);

  const handleClick = useCallback((index) => {
    sliderRef.current?.slickGoTo(index);
  }, []);

  const handleReplay = useCallback(() => {
    setShowEnded(false);
    video1ref.current?.seekTo(0);
    setIsPlaying(true);
  }, []);

  const handleEnd = useCallback(() => {
    setShowEnded(true);
    setIsPlaying(false);
  }, []);

  const closeModal = useCallback(() => {
    setPopup(false);
  }, []);

  return (
    <>
      <Navbar />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="z-[1] md:py-32 py-20 mx-auto grid grid-cols-1 custom:grid-cols-2 custom:px-20 px-6 gap-8">
          {/* Hero content */}
          <div className="flex gap-10 px-6 md:px-10 flex-col items-center justify-center">
            <h1
              className="text-3xl text-center md:text-5xl lg:text-6xl font-bold leading-tight"
              style={{ color: '#0f172a' }}
              data-aos="zoom-y-out"
            >
              Conduct{" "}
              <span 
                style={{ 
                  background: 'linear-gradient(to right, #2563eb, #14b8a6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                faster, more accurate
              </span>{" "}
              trials with{" "}
              <span 
                style={{ 
                  background: 'linear-gradient(to right, #2563eb, #14b8a6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                fewer patients
              </span>
            </h1>

            <PrimaryBtn
              onClick={() => setPopup(true)}
              text="Request a Demo"
            />
          </div>

          <Popup
            onClose={closeModal}
            modal
            open={popupOpen}
            className="rounded-xl"
            position="center center"
          >
            <DemoContact handleClose={closeModal} />
          </Popup>

          {/* Video Player */}
          <div className="flex justify-center mt-12 lg:mt-16 px-4">
            <div className="w-full" style={{ maxWidth: "640px" }}>
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <ReactPlayer
                  id="introVideo"
                  url="https://www.youtube.com/watch?v=V55AWCNqC3E&feature=youtu.be"
                  config={{
                    youtube: {
                      playerVars: {
                        modestbranding: 1,
                        rel: 0,
                        showinfo: 0,
                        iv_load_policy: 3,
                      },
                    },
                  }}
                  onEnded={handleEnd}
                  width="100%"
                  height="100%"
                  className="absolute inset-0"
                  ref={video1ref}
                  playsinline
                  onReady={handleReady}
                  playing={isPlaying}
                  onPlay={() => setIsPlaying(true)}
                  controls={true}
                />
                {showEnded && (
                  <div
                    onClick={handleReplay}
                    className="absolute left-0 top-0 w-full h-full flex items-center justify-center bg-black/50"
                  >
                    <MdOutlineReplay size={80} className="cursor-pointer text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Slider Section */}
      <section className="relative overflow-hidden py-20 bg-gray-50">
        {/* Solution Tabs */}
        <div className="flex flex-none md:flex-wrap flex-nowrap overflow-x-auto justify-center md:gap-4 gap-2 items-center px-4 md:px-10 py-4">
          {SolutionsList.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleClick(index)}
              className="font-medium md:text-lg text-sm whitespace-nowrap rounded-lg text-center px-4 py-2 cursor-pointer transition-all"
              style={currentToggle === index 
                ? { backgroundColor: '#0f172a', color: '#ffffff', border: '1px solid #0f172a' }
                : { backgroundColor: '#ffffff', color: '#374151', border: '1px solid #e5e7eb' }
              }
            >
              {item.text}
            </button>
          ))}
        </div>

        {/* Slider Content */}
        <div className="md:px-20 px-10 h-full justify-center flex flex-col relative mt-8">
          <Slider ref={sliderRef} {...settings}>
            {SolutionsList.map((item) => (
              <div key={item.id} className="md:px-4 px-0 py-10 w-full">
                <div className="grid grid-cols-1 custom:grid-cols-2 gap-8">
                  <div className="flex h-full justify-center col-span-1 flex-col items-center gap-4">
                    <h3 
                      className="md:text-3xl text-xl font-semibold text-center"
                      style={{ color: '#0f172a' }}
                    >
                      {item.title}
                    </h3>
                    <p 
                      className="md:text-xl text-base text-center max-w-lg"
                      style={{ color: '#475569' }}
                    >
                      {item.description}
                    </p>
                  </div>
                  <div className="col-span-1 flex justify-center items-center">
                    {item.video ? (
                      <div className="relative md:w-[35rem] md:h-80 h-48 w-full overflow-hidden rounded-lg shadow-lg">
                        <ReactPlayer
                          url={item.video}
                          width="100%"
                          height="100%"
                          style={{ position: 'absolute', top: '0', left: '0' }}
                          muted={true}
                          loop
                          playsinline
                          playing={true}
                          controls={false}
                        />
                      </div>
                    ) : (
                      <img
                        src={item.img}
                        className="md:h-80 h-48 object-contain"
                        alt={item.title}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>

      <SolutionsTextSection />
      <ContactFooter />
      <Footer />
    </>
  );
}
